import {
  Box,
  ChevronLeftIcon,
  ChevronRightIcon,
  DataTable,
  DataTableEmpty,
  Grid,
  Typography,
  useTheme,
} from '@silverstein-properties/inspirelabs-ui';
import { useState } from 'react';
import { useGetAllMerchantBookings } from '@/hooks/queries/useBookings';
import {
  getCoreRowModel,
  PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import { getTableColumns } from './CustomersTable.columns';
import { formatDateWithDayName } from '@/utils';
import { DataTableLoading, Pagination } from '@/components';
import { DEFAULT_SEARCH_PARAMS } from '@/constants';
import { addDays } from 'date-fns';
import { TableWrapper } from './MerchantCustomers.styles';

export const MerchantCustomers = ({
  selectedProductIds,
  selectedDate,
  onChangeSelectedDate,
}: {
  selectedProductIds?: string[];
  selectedDate: Date;
  onChangeSelectedDate: (date: Date) => void;
}) => {
  const theme = useTheme();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: DEFAULT_SEARCH_PARAMS.PAGE,
    pageSize: DEFAULT_SEARCH_PARAMS.PAGE_SIZE,
  });

  const { data: merchantBookings, isLoading } = useGetAllMerchantBookings({
    limit: pagination.pageSize,
    page: pagination.pageIndex,
    startDate: selectedDate.toISOString(),
    productIds: selectedProductIds,
  });

  const pageCount = merchantBookings?.metadata?.totalItems
    ? Math.ceil(merchantBookings?.metadata?.totalItems / pagination.pageSize)
    : -1;

  const table = useReactTable({
    data: merchantBookings?.data || [],
    columns: getTableColumns(),
    pageCount: pageCount,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    manualPagination: true,
  });

  const handleChangeDay = (type: 'prev' | 'next') => {
    onChangeSelectedDate(addDays(selectedDate, type === 'prev' ? -1 : 1));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: theme.spacers.m }}>
        <Typography
          sx={{ mr: theme.spacers.m }}
          variant="body1"
          fontWeight={theme.typography.fontWeightMedium}
        >
          {formatDateWithDayName(selectedDate)}
        </Typography>
        <ChevronLeftIcon
          width={24}
          height={24}
          onClick={() => handleChangeDay('prev')}
        />
        <ChevronRightIcon
          width={24}
          height={24}
          onClick={() => handleChangeDay('next')}
        />
      </Box>

      <Grid container item xs={12}>
        <TableWrapper
          item
          xs={12}
          sx={{
            '& th': {
              backgroundColor: theme.palette.background.paper,
            },
            '& td': {
              padding: theme.spacers.m,
            },
          }}
        >
          <DataTable table={table} rightAlignedColumns={[]} />
          <DataTableLoading show={isLoading} numberOfSkeletons={10} />
          <DataTableEmpty
            show={
              !isLoading &&
              (!merchantBookings || merchantBookings?.data.length === 0)
            }
          />
          <Pagination
            show={true}
            table={table}
            totalItems={merchantBookings?.metadata?.totalItems || 0}
          />
        </TableWrapper>
      </Grid>
    </Box>
  );
};
